/**
 * PhoneInput — 10-digit India phone input with inline validation.
 *
 * - Strips non-digits as the user types
 * - Caps at 10 digits
 * - Shows inline red error when length is 1-9 and the field has lost focus or has been touched
 * - `onChange` fires with the cleaned digit-only string
 * - `onValidityChange(isValid)` lets the parent disable Save buttons until valid
 *
 * Usage:
 *   <PhoneInput value={form.phone} onChange={v => setForm({...form, phone:v})} required />
 */
import { useEffect, useState } from 'react';
import { Input } from './ui/input';

const stripDigits = (s) => (s || '').toString().replace(/\D/g, '').slice(0, 10);

const PhoneInput = ({ value, onChange, onValidityChange, required = false, placeholder = '10-digit mobile', testid, className = '', ...rest }) => {
  const [touched, setTouched] = useState(false);
  const clean = stripDigits(value);
  const len = clean.length;
  const isEmpty = len === 0;
  const isValid = len === 10 || (!required && isEmpty);
  const hasError = touched && !isValid;

  useEffect(() => { onValidityChange?.(isValid); /* eslint-disable-next-line */ }, [isValid]);

  return (
    <div>
      <Input
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        maxLength={10}
        placeholder={placeholder}
        value={clean}
        onChange={(e) => onChange?.(stripDigits(e.target.value))}
        onBlur={() => setTouched(true)}
        data-testid={testid}
        className={`${className} ${hasError ? 'border-rose-400 focus-visible:ring-rose-300' : ''}`}
        {...rest}
      />
      {hasError && (
        <p className="text-[10px] text-rose-600 mt-1 font-medium" data-testid={testid ? `${testid}-error` : undefined}>
          {isEmpty ? 'Phone number is required' : `Please enter all 10 digits (currently ${len} digit${len === 1 ? '' : 's'})`}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
